import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  Optional,
  Output,
  Type,
} from '@angular/core';
import { ControlContainer, NgForm, NgModelGroup } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { FroalaViewModule } from 'angular-froala-wysiwyg';
import DOMPurify from 'dompurify';
import { LinksService } from 'libs/shared/domain/src/lib/infrastructure/links.service';
import { PopoverService } from 'libs/shared/ui-common/src/lib/components/popover/popover.service';
import { updateReferenceNumbersInText } from 'shared/feature-rich-text-editor';

import { NODE_DETAIL_POPOVER_COMPONENT, NodeDetailPopoverData, Reference, ReferenceWithNumber } from '@treetop/shared/model';
import { ReferenceNumberingService } from '@treetop/shared/reference-numbering';

import { FroalaViewDirective } from './froala-view.directive';

@Component({
  selector: 'common-rich-text-view',
  standalone: true,
  imports: [FroalaViewModule, FroalaViewDirective, TranslocoPipe],
  templateUrl: './rich-text-view.component.html',
  styleUrls: ['./rich-text-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (ngForm: NgForm, ngModelGroup: NgModelGroup) => ngModelGroup || ngForm || null,
      deps: [
        [new Optional(), NgForm],
        [new Optional(), NgModelGroup],
      ],
    },
  ],
})
export class RichTextViewComponent implements AfterViewInit {
  private referenceNumberingService = inject(ReferenceNumberingService, { optional: true });
  private popoverService = inject(PopoverService);
  private elementRef = inject(ElementRef);
  private linksService = inject(LinksService, { optional: true });
  private nodeDetailPopoverComponent = inject<Type<unknown>>(NODE_DETAIL_POPOVER_COMPONENT, { optional: true });

  public content = input<string>('');

  @Input() public placeholder = 'NoContent';
  @Output() public initialized = new EventEmitter<boolean>();

  /**
   * Only needed when reference handling is required for displaying reference numbers.
   */
  public references = input<Reference[] | undefined>([]);

  private numberedReferences = computed(() => {
    if (!this.references() || !this.referenceNumberingService?.orderedReferences()) {
      return [];
    }

    return this.references().map((reference: Reference) => {
      return this.referenceNumberingService?.orderedReferences().find((r) => r.id === reference.id);
    });
  });

  protected contentWithUpdatedReferenceNumbers = computed(() => {
    return updateReferenceNumbersInText(this.content(), this.numberedReferences());
  });

  protected sanitizedContent = computed(() => {
    return DOMPurify.sanitize(this.contentWithUpdatedReferenceNumbers());
  });

  public ngAfterViewInit(): void {
    const anchorLinks = document.querySelectorAll('a[data-element-id]');
    anchorLinks.forEach((anchor) => {
      anchor.addEventListener('click', (event) => {
        event.preventDefault();
        this.linksService?.openAnchorLinkFromTextEditor(event.target as HTMLElement);
      });
    });

    const referenceElements = this.elementRef.nativeElement.querySelectorAll('[data-tt-custom-element][data-reference-id]');
    referenceElements.forEach((referenceElement) => {
      referenceElement.addEventListener('click', (event) => {
        const dataRefId = (event.target as HTMLElement).getAttribute('data-reference-id');
        const orderedReferences = this.referenceNumberingService?.orderedReferences() ?? [];
        const ref = orderedReferences.filter((reference) => reference.id === dataRefId);
        if (ref && ref.length > 0) {
          this.openReferenceListPopover(ref, referenceElement as HTMLElement);
        }
      });
    });

    this.initialized.emit();
  }

  private openReferenceListPopover(references: ReferenceWithNumber[], target: HTMLElement) {
    if (!this.nodeDetailPopoverComponent) return;

    this.popoverService.open<NodeDetailPopoverData>(this.nodeDetailPopoverComponent, target, {
      data: { references },
    });
  }
}
